// Mesh-construction helpers for the selection cube + Stage 5 registry
// wireframes. Pure: no listeners, no state mutation beyond the geometry
// returned.

import * as THREE from 'three';
import { state } from './state';
import { physicalToThreeJS } from '@adapters/coordinates';
import { RENDER_ORDER } from '../renderOrder';
import type { BoxSelection, SphereSelection } from '@store/schema';

export function createCubeEdges(size: number): THREE.Group {
    const T = state.THREE ?? THREE;
    const edgeGroup = new T.Group();
    const s = size / 2;
    const corners: [number, number, number][] = [
        [-s, -s, -s], [s, -s, -s], [s, s, -s], [-s, s, -s],
        [-s, -s, s],  [s, -s, s],  [s, s, s],  [-s, s, s],
    ];
    const edges: [number, number][] = [[0,1],[1,2],[2,3],[3,0],[4,5],[5,6],[6,7],[7,4],[0,4],[1,5],[2,6],[3,7]];
    const edgeMaterial = new T.MeshBasicMaterial({ color: 0xffff00, transparent: true, opacity: 0.4, depthWrite: false });
    edges.forEach((edge) => {
        const a = corners[edge[0]]!; const b = corners[edge[1]]!;
        const start = new T.Vector3(a[0], a[1], a[2]);
        const end = new T.Vector3(b[0], b[1], b[2]);
        const direction = new T.Vector3().subVectors(end, start);
        const length = direction.length();
        const cylinderGeometry = new T.CylinderGeometry(0.02, 0.02, length, 8);
        const cylinder = new T.Mesh(cylinderGeometry, edgeMaterial);
        const midpoint = new T.Vector3().addVectors(start, end).multiplyScalar(0.5);
        cylinder.position.copy(midpoint);
        cylinder.lookAt(end);
        cylinder.rotateX(Math.PI / 2);
        edgeGroup.add(cylinder);
    });
    return edgeGroup;
}

// ─── Stage 5 selection wireframes ──────────────────────────────────────────

/**
 * Build a thin axis-aligned box wireframe in Three.js coords. Caller supplies
 * the shared material; the returned mesh carries `userData.selectionId` so
 * future picking can dispatch a focus intent.
 *
 * NOTE: the box is axis-aligned in *physical* coords. Each corner maps through
 * `physicalToThreeJS` independently so the resulting mesh follows the same
 * coord-rotation as the data grid (avoids skew when the dataset isn't axis-
 * aligned with the Three.js basis).
 */
export function createBoxWireframe(box: BoxSelection, material: THREE.LineBasicMaterial): THREE.LineSegments {
    const [lx, ly, lz] = box.lower;
    const [ux, uy, uz] = box.upper;
    const corners = [
        [lx, ly, lz], [ux, ly, lz], [ux, uy, lz], [lx, uy, lz],
        [lx, ly, uz], [ux, ly, uz], [ux, uy, uz], [lx, uy, uz],
    ];
    const mapped = corners.map(([x, y, z]) => physicalToThreeJS(x!, y!, z!));
    const edges: [number, number][] = [
        [0, 1], [1, 2], [2, 3], [3, 0],
        [4, 5], [5, 6], [6, 7], [7, 4],
        [0, 4], [1, 5], [2, 6], [3, 7],
    ];
    const positions: number[] = [];
    for (const [a, b] of edges) {
        const pa = mapped[a]!;
        const pb = mapped[b]!;
        positions.push(pa.x, pa.y, pa.z, pb.x, pb.y, pb.z);
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    const mesh = new THREE.LineSegments(geom, material);
    mesh.name = `SelectionBox_${box.id}`;
    mesh.userData['selectionId'] = box.id;
    mesh.renderOrder = RENDER_ORDER.SELECTION_WIREFRAME;
    return mesh;
}

/**
 * Build a sphere wireframe in Three.js coords using an icosahedron at detail=1
 * (80 edges, geodesic — reads as "sphere" at any zoom without lat/long pole
 * artefacts). Center placed via `physicalToThreeJS`.
 */
export function createSphereWireframe(sphere: SphereSelection, material: THREE.LineBasicMaterial): THREE.LineSegments {
    // Approximate a single physical-radius — assumes the coord transform is
    // pure rotation + uniform scale. Holds today (identity scale on all axes);
    // future datasets with per-axis scaling render this as a sphere where it
    // should be an ellipsoid. The warning below makes that case visible.
    warnIfNonUniformScale();
    const geom = new THREE.WireframeGeometry(new THREE.IcosahedronGeometry(sphere.radius, 1));
    const mesh = new THREE.LineSegments(geom, material);
    const center = physicalToThreeJS(sphere.center[0], sphere.center[1], sphere.center[2]);
    mesh.position.copy(center);
    mesh.name = `SelectionSphere_${sphere.id}`;
    mesh.userData['selectionId'] = sphere.id;
    mesh.renderOrder = RENDER_ORDER.SELECTION_WIREFRAME;
    return mesh;
}

let _nonUniformScaleWarned = false;
/** One-shot warning if `physicalToThreeJS` applies non-uniform per-axis
 *  scale — the sphere wireframe path assumes uniform scale and will look
 *  spherical even when the data extent is anisotropic. */
function warnIfNonUniformScale(): void {
    if (_nonUniformScaleWarned) return;
    const origin = physicalToThreeJS(0, 0, 0);
    const sx = physicalToThreeJS(1, 0, 0).sub(origin).length();
    const sy = physicalToThreeJS(0, 1, 0).sub(origin).length();
    const sz = physicalToThreeJS(0, 0, 1).sub(origin).length();
    const max = Math.max(sx, sy, sz);
    const min = Math.min(sx, sy, sz);
    if (max - min > 1e-6 * Math.max(max, 1)) {
        _nonUniformScaleWarned = true;
        // eslint-disable-next-line no-console
        console.warn(`[magviz] Sphere selection wireframe assumes uniform coord scale; got (${sx.toFixed(4)}, ${sy.toFixed(4)}, ${sz.toFixed(4)}) — wireframe will read as a sphere instead of an ellipsoid.`);
    }
}
